import React, { useEffect, useRef, useState, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate } from "react-router-dom";
import "./addExperienceCenter.css";
import { addNewExperienceCenter } from "../../Redux/Actions/experienceCenterActions";
import { useDispatch, useSelector } from "react-redux";
import {
    viewCityListPagination,
    viewCountryListPagination,
    viewStateListPagination,
} from "../../Redux/Actions/settingsActions";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";

/* ── Google Maps config ─────────────────────────────────────────────────── */
const MAP_LIBRARIES = ["places"];
const MAP_CONTAINER_STYLE = {
    width: "100%",
    height: "380px",
    borderRadius: "10px",
};
const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 }; // New Delhi fallback

/* ─────────────────────────────────────────────────────────────────────────── */

const AddExperienceCenter = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const coverInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const mapRef = useRef(null);
    const autocompleteRef = useRef(null);

    const { countryList } = useSelector((state) => state.country);
    const { states } = useSelector((state) => state.states);
    const cityState = useSelector((state) => state.city || {});
    const cities = Array.isArray(cityState?.citiesAllList?.data?.data)
        ? cityState.citiesAllList.data.data
        : [];
    const { loading = false } = useSelector((state) => state.experienceCenter || {});

    /* ── Google Maps load ───────────────────────────────────────────────── */
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: MAP_LIBRARIES,
    });

    /* ── UI state ───────────────────────────────────────────────────────── */
    const [activeSection, setActiveSection] = useState(0);
    const [errors, setErrors] = useState({});

    const [coverPreview, setCoverPreview] = useState(null);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [coverDragOver, setCoverDragOver] = useState(false);

    const [videoPreview, setVideoPreview] = useState(null);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [videoDragOver, setVideoDragOver] = useState(false);
    const [images, setImages] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const imageInputRef = useRef(null);

    /* ── Map state ──────────────────────────────────────────────────────── */
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
    const [markerPosition, setMarkerPosition] = useState(null);

    /* ── Form data ──────────────────────────────────────────────────────── */
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        type: "",
        address: "",
        cover_image: "",
        video: "",
        image: [],
        country: "",
        state: "",
        city: "",
        // location GeoJSON – matches schema
        location: {
            type: "Point",
            coordinates: [0, 0], // [lng, lat]
        },
        google_url: "",
    });

    const [stateData, setStateData] = useState({ country: "" });
    const [cityData, setCityData] = useState({ state: "", country: "", page: 1, limit: 1000 });

    /* ── Effects ────────────────────────────────────────────────────────── */
    useEffect(() => { dispatch(viewCountryListPagination()); }, [dispatch]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (stateData.country !== "") dispatch(viewStateListPagination(stateData));
        }, 300);
        return () => clearTimeout(t);
    }, [stateData.country, dispatch, stateData]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (cityData.state !== "") dispatch(viewCityListPagination(cityData));
        }, 300);
        return () => clearTimeout(t);
    }, [cityData, cityData.state, dispatch]);

    /* ── Helpers ────────────────────────────────────────────────────────── */
    const clearError = (field) =>
        setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "country") {
            setStateData({ country: value });
            setCityData((prev) => ({ ...prev, country: value, state: "" }));
            setFormData((prev) => ({ ...prev, country: value, state: "", city: "" }));
            clearError("country");
            return;
        }
        if (name === "state") {
            setCityData((prev) => ({ ...prev, state: value }));
            setFormData((prev) => ({ ...prev, state: value, city: "" }));
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
        clearError(name);
    };

    const processImageFiles = async (files) => {
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);
        const remaining = 5 - images.length;

        if (remaining <= 0) {
            toast.error("Maximum 5 images allowed");
            return;
        }

        const toUpload = fileArray.slice(0, remaining);

        const invalidFiles = toUpload.filter((f) => !f.type.startsWith("image/"));
        if (invalidFiles.length > 0) {
            toast.error("Please upload image files only");
            return;
        }

        try {
            setUploadingImages(true);
            const uploadedUrls = await Promise.all(toUpload.map((file) => AddImage(file)));
            const newImages = [...images, ...uploadedUrls];
            setImages(newImages);
            setFormData((prev) => ({ ...prev, image: newImages }));
            clearError("image");
        } catch {
            toast.error("Image upload failed");
        } finally {
            setUploadingImages(false);
            if (imageInputRef.current) imageInputRef.current.value = "";
        }
    };

    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        setImages(newImages);
        setFormData((prev) => ({ ...prev, image: newImages }));
    };

    const handleImageFileChange = (e) => processImageFiles(e.target.files);

    const handleImageDrop = (e) => {
        e.preventDefault();
        processImageFiles(e.dataTransfer.files);
    };

    /* ── Google Maps handlers ───────────────────────────────────────────── */
    const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

    const geocodeLatLng = useCallback((lat, lng) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
                setFormData((prev) => ({
                    ...prev,
                    address: prev.address || results[0].formatted_address,
                    location: { type: "Point", coordinates: [lng, lat] },
                    google_url: `https://maps.google.com/?q=${lat},${lng}`,
                }));
                clearError("location");
            }
        });
    }, []); // eslint-disable-line

    const onMapClick = useCallback((event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setMarkerPosition({ lat, lng });
        setFormData((prev) => ({
            ...prev,
            location: { type: "Point", coordinates: [lng, lat] },
            google_url: `https://maps.google.com/?q=${lat},${lng}`,
        }));
        geocodeLatLng(lat, lng);
    }, [geocodeLatLng]);

    const onAutocompleteLoad = (ac) => { autocompleteRef.current = ac; };

    const onPlaceChanged = () => {
        if (!autocompleteRef.current) return;
        const place = autocompleteRef.current.getPlace();
        if (!place.geometry) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setMarkerPosition({ lat, lng });
        setMapCenter({ lat, lng });

        setFormData((prev) => ({
            ...prev,
            address: place.formatted_address || prev.address,
            location: { type: "Point", coordinates: [lng, lat] },
            google_url: `https://maps.google.com/?q=${lat},${lng}`,
        }));
        clearError("location");
        clearError("address");
    };

    /* ── File upload helpers ────────────────────────────────────────────── */
    const processCoverFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
        setCoverPreview(URL.createObjectURL(file));
        try {
            setUploadingCover(true);
            const url = await AddImage(file);
            setFormData((prev) => ({ ...prev, cover_image: url }));
            clearError("cover_image");
        } catch {
            toast.error("Cover image upload failed");
            setCoverPreview(null);
            setFormData((prev) => ({ ...prev, cover_image: "" }));
        } finally { setUploadingCover(false); }
    };

    const processVideoFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("video/")) { toast.error("Please upload a video file"); return; }
        setVideoPreview(URL.createObjectURL(file));
        try {
            setUploadingVideo(true);
            const url = await AddImage(file);
            setFormData((prev) => ({ ...prev, video: url }));
            clearError("video");
        } catch {
            toast.error("Video upload failed");
            setVideoPreview(null);
            setFormData((prev) => ({ ...prev, video: "" }));
        } finally { setUploadingVideo(false); }
    };

    const handleCoverFileChange = (e) => processCoverFile(e.target.files?.[0]);
    const handleCoverDrop = (e) => { e.preventDefault(); setCoverDragOver(false); processCoverFile(e.dataTransfer.files?.[0]); };
    const removeCover = () => { setCoverPreview(null); setFormData((prev) => ({ ...prev, cover_image: "" })); if (coverInputRef.current) coverInputRef.current.value = ""; };

    const handleVideoFileChange = (e) => processVideoFile(e.target.files?.[0]);
    const handleVideoDrop = (e) => { e.preventDefault(); setVideoDragOver(false); processVideoFile(e.dataTransfer.files?.[0]); };
    const removeVideo = () => { setVideoPreview(null); setFormData((prev) => ({ ...prev, video: "" })); if (videoInputRef.current) videoInputRef.current.value = ""; };

    /* ── Section navigation ─────────────────────────────────────────────── */
    const sections = ["Basic Info", "Location & Address"];

    const handleNextSection = () => {
        if (activeSection === 0) {
            const sectionErrors = {};
            if (!formData.title.trim()) sectionErrors.title = "Title is required";
            if (!formData.type) sectionErrors.type = "Type is required";
            if (Object.keys(sectionErrors).length) { setErrors((prev) => ({ ...prev, ...sectionErrors })); return; }
        }
        setActiveSection((s) => Math.min(sections.length - 1, s + 1));
    };

    const handleReset = () => {
        setFormData({ title: "", description: "", type: "", address: "", cover_image: "", video: "", image: [], country: "", state: "", city: "", location: { type: "Point", coordinates: [0, 0] }, google_url: "" });
        setActiveSection(0);
        setStateData({ country: "" });
        setCityData({ state: "", country: "", page: 1, limit: 1000 });
        setErrors({});
        setImages([]);
        setCoverPreview(null);
        setVideoPreview(null);
        setMarkerPosition(null);
        setMapCenter(DEFAULT_CENTER);
        if (coverInputRef.current) coverInputRef.current.value = "";
        if (videoInputRef.current) videoInputRef.current.value = "";
    };

    /* ── Validation ─────────────────────────────────────────────────────── */
    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (!formData.type) newErrors.type = "Type is required";
        if (!formData.address.trim()) newErrors.address = "Address is required";
        if (!formData.country) newErrors.country = "Country is required";
        if (!formData.state) newErrors.state = "State is required";
        if (!images || images.length < 1) newErrors.image = "Please upload at least 1 image";
        if (!formData.city) newErrors.city = "City is required";
        if (formData.open_timing && formData.close_timing) {
            if (formData.close_timing <= formData.open_timing) {
                alert("Close time must be greater than open time");
                return;
            }
        }
        const [lng, lat] = formData.location.coordinates;
        if (lng === 0 && lat === 0) newErrors.location = "Please select a location on the map";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ── Submit ─────────────────────────────────────────────────────────── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            if (!formData.title.trim()) { setActiveSection(0); }
            else { setActiveSection(1); }
            return;
        }

        try {
            const payload = {
                title: formData.title,
                description: formData.description,
                type: formData.type,
                address: formData.address,
                cover_image: formData.cover_image,
                video: formData.video,
                image: formData.image,
                country: formData.country,
                state: formData.state,
                city: formData.city,
                location: formData.location,
                open_timing: formData.open_timing
                    ? new Date(`1970-01-01T${formData.open_timing}:00`)
                    : null,
                close_timing: formData.close_timing
                    ? new Date(`1970-01-01T${formData.close_timing}:00`)
                    : null,
            };
            if (formData.google_url) payload.google_url = formData.google_url;

            const response = await dispatch(addNewExperienceCenter(payload));
            if (response?.statusCode === 200) {
                toast.success("Experience center added successfully!");
                handleReset();
                navigate("/experience_center");
            }
        } catch (error) {
            const msg = error?.response?.data?.message || error?.message || "Failed to add experience center";
            if (msg.toLowerCase().includes("duplicate") || msg.toLowerCase().includes("already")) {
                setErrors((prev) => ({ ...prev, title: "Title already exists" }));
                setActiveSection(0);
                return toast.error("Title already exists");
            }
            toast.error(msg);
        }
    };

    /* ── Coordinate display helpers ─────────────────────────────────────── */
    const [lng, lat] = formData.location.coordinates;
    const hasLocation = !(lng === 0 && lat === 0);

    /* ── Render ─────────────────────────────────────────────────────────── */
    return (
        <MainLayout>
            <div className="aec-wrapper">
                {/* Header */}
                <div className="aec-header">
                    <div className="aec-header-left">
                        <div className="aec-header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                <polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="aec-title">Add Experience Center</h1>
                            <p className="aec-subtitle">Fill in the details to add a new experience center</p>
                        </div>
                    </div>
                    <Link to="/experience_center" className="aec-view-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        </svg>
                        View Centers
                    </Link>
                </div>

                {/* Section Nav */}
                <div className="aec-nav">
                    {sections.map((sec, i) => (
                        <button key={i} type="button"
                            className={`aec-nav-pill ${activeSection === i ? "aec-nav-pill--active" : ""}`}
                            onClick={() => setActiveSection(i)}
                            disabled={loading || uploadingCover || uploadingVideo}>
                            <span className="aec-nav-num">{String(i + 1).padStart(2, "0")}</span>
                            {sec}
                        </button>
                    ))}
                </div>

                <form className="aec-form">
                    {/* ── Section 1: Basic Info ── */}
                    <div className={`aec-section ${activeSection === 0 ? "aec-section--visible" : ""}`}>
                        <div className="aec-section-header">
                            <span className="aec-section-badge">01</span>
                            <h2 className="aec-section-title">Basic Information</h2>
                        </div>

                        <div className="aec-grid aec-grid-2">
                            <div className="aec-field aec-field--full">
                                <label className="aec-label">Title <span className="aec-req">*</span></label>
                                <input className={`aec-input ${errors.title ? "aec-input--error" : ""}`} type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Downtown Experience Center" disabled={loading} />
                                {errors.title && <span className="aec-error-msg">{errors.title}</span>}
                            </div>
                            <div className="aec-field aec-field--full">
                                <label className="aec-label">Type <span className="aec-req">*</span></label>
                                <select
                                    className={`aec-select ${errors.type ? "aec-input--error" : ""}`}
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select type</option>
                                    <option value="available">Available</option>
                                    <option value="coming_soon">Coming Soon</option>
                                </select>
                                {errors.type && <span className="aec-error-msg">{errors.type}</span>}
                            </div>
                            <div className="aec-field aec-field--full">
                                <label className="aec-label">Description <span className="aec-optional">(Optional)</span></label>
                                <textarea className="aec-textarea" name="description" value={formData.description} onChange={handleChange} placeholder="Describe the experience center..." disabled={loading} rows={3} />
                            </div>

                            <div className="form-group">
                                <label>Open Time</label>
                                <input
                                    type="time"
                                    name="open_timing"
                                    className="form-control"
                                    value={formData.open_timing || ""}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label>Close Time</label>
                                <input
                                    type="time"
                                    name="close_timing"
                                    className="form-control"
                                    value={formData.close_timing || ""}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Cover Image */}
                            <div className="aec-field aec-field--full">
                                <label className="aec-label">Cover Image <span className="aec-optional">(Optional)</span></label>
                                {coverPreview ? (
                                    <div className="aec-preview">
                                        <div className="aec-preview-img-wrap">
                                            {uploadingCover && (
                                                <div className="aec-uploading-overlay">
                                                    <svg className="aec-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                                    </svg>
                                                    <span>Uploading...</span>
                                                </div>
                                            )}
                                            <img src={coverPreview} alt="Cover preview" className="aec-preview-img" />
                                        </div>
                                        <div className="aec-preview-info">
                                            <span className="aec-preview-label">Cover image uploaded</span>
                                            {formData.cover_image && <span className="aec-preview-url">{formData.cover_image.slice(0, 48)}...</span>}
                                            <div className="aec-preview-actions">
                                                <button type="button" className="aec-change-btn" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover || loading}>Change</button>
                                                <button type="button" className="aec-remove-btn" onClick={removeCover} disabled={uploadingCover || loading}>Remove</button>
                                            </div>
                                        </div>
                                        <input ref={coverInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCoverFileChange} />
                                    </div>
                                ) : (
                                    <div className={`aec-dropzone ${coverDragOver ? "aec-dropzone--over" : ""}`} onClick={() => coverInputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setCoverDragOver(true); }} onDragLeave={() => setCoverDragOver(false)} onDrop={handleCoverDrop} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && coverInputRef.current?.click()}>
                                        <div className="aec-dropzone-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                        </div>
                                        <div className="aec-dropzone-text">
                                            <span className="aec-dropzone-title">{coverDragOver ? "Drop image here" : "Upload cover image"}</span>
                                            <span className="aec-dropzone-sub">Click to browse or drag & drop · PNG, JPG, WebP</span>
                                        </div>
                                        <input ref={coverInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCoverFileChange} />
                                    </div>
                                )}
                            </div>

                            {/* Video */}
                            <div className="aec-field aec-field--full">
                                <label className="aec-label">Video <span className="aec-optional">(Optional)</span></label>
                                {videoPreview ? (
                                    <div className="aec-preview">
                                        <div className="aec-preview-video-wrap">
                                            {uploadingVideo && (
                                                <div className="aec-uploading-overlay">
                                                    <svg className="aec-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                                    </svg>
                                                    <span>Uploading...</span>
                                                </div>
                                            )}
                                            <video src={videoPreview} className="aec-preview-video" muted />
                                        </div>
                                        <div className="aec-preview-info">
                                            <span className="aec-preview-label">Video uploaded</span>
                                            {formData.video && <span className="aec-preview-url">{formData.video.slice(0, 48)}...</span>}
                                            <div className="aec-preview-actions">
                                                <button type="button" className="aec-change-btn" onClick={() => videoInputRef.current?.click()} disabled={uploadingVideo || loading}>Change</button>
                                                <button type="button" className="aec-remove-btn" onClick={removeVideo} disabled={uploadingVideo || loading}>Remove</button>
                                            </div>
                                        </div>
                                        <input ref={videoInputRef} type="file" accept="video/*" style={{ display: "none" }} onChange={handleVideoFileChange} />
                                    </div>
                                ) : (
                                    <div className={`aec-dropzone ${videoDragOver ? "aec-dropzone--over" : ""}`} onClick={() => videoInputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setVideoDragOver(true); }} onDragLeave={() => setVideoDragOver(false)} onDrop={handleVideoDrop} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && videoInputRef.current?.click()}>
                                        <div className="aec-dropzone-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <polygon points="23 7 16 12 23 17 23 7" />
                                                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                            </svg>
                                        </div>
                                        <div className="aec-dropzone-text">
                                            <span className="aec-dropzone-title">{videoDragOver ? "Drop video here" : "Upload video"}</span>
                                            <span className="aec-dropzone-sub">Click to browse or drag & drop · MP4, MOV, WebM</span>
                                        </div>
                                        <input ref={videoInputRef} type="file" accept="video/*" style={{ display: "none" }} onChange={handleVideoFileChange} />
                                    </div>
                                )}
                            </div>

                            <div className="aec-field aec-field--full">
                                <label className="aec-label">
                                    Images <span className="aec-req">*</span>
                                    <span className="aec-optional"> (Min 1, Max 5)</span>
                                </label>

                                {images.length > 0 && (
                                    <div className="aec-image-grid">
                                        {images.map((url, index) => (
                                            <div key={index} className="aec-image-thumb-wrap">
                                                <img src={url} alt={`uploaded-${index}`} className="aec-image-thumb" />
                                                <button
                                                    type="button"
                                                    className="aec-image-remove-btn"
                                                    onClick={() => removeImage(index)}
                                                    disabled={uploadingImages || loading}
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}

                                        {images.length < 5 && (
                                            <div
                                                className="aec-image-add-btn"
                                                onClick={() => imageInputRef.current?.click()}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <line x1="12" y1="5" x2="12" y2="19" />
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                </svg>
                                                <span>Add More</span>
                                                <input
                                                    ref={imageInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    style={{ display: "none" }}
                                                    onChange={handleImageFileChange}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {images.length === 0 && (
                                    <div
                                        className={`aec-dropzone`}
                                        onClick={() => imageInputRef.current?.click()}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={handleImageDrop}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === "Enter" && imageInputRef.current?.click()}
                                    >
                                        <div className="aec-dropzone-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                        </div>
                                        <div className="aec-dropzone-text">
                                            <span className="aec-dropzone-title">
                                                {uploadingImages ? "Uploading..." : "Upload images"}
                                            </span>
                                            <span className="aec-dropzone-sub">
                                                Click to browse or drag & drop · Min 1, Max 5 · PNG, JPG, WebP
                                            </span>
                                        </div>
                                        <input
                                            ref={imageInputRef}
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            style={{ display: "none" }}
                                            onChange={handleImageFileChange}
                                        />
                                    </div>
                                )}

                                {uploadingImages && (
                                    <p className="aec-upload-progress">Uploading images, please wait...</p>
                                )}

                                {errors.image && <span className="aec-error-msg">{errors.image}</span>}
                            </div>
                        </div>
                    </div>

                    {/* ── Section 2: Location & Address ── */}
                    <div className={`aec-section ${activeSection === 1 ? "aec-section--visible" : ""}`}>
                        <div className="aec-section-header">
                            <span className="aec-section-badge">02</span>
                            <h2 className="aec-section-title">Location & Address Details</h2>
                        </div>

                        <div className="aec-grid aec-grid-2">
                            {/* Google Map */}
                            <div className="aec-field aec-field--full">
                                <label className="aec-label">
                                    Pin Location on Map
                                    <span className="aec-req"> *</span>
                                </label>

                                {/* Map loading / error states */}
                                {loadError && (
                                    <div className="aec-map-placeholder aec-map-placeholder--error">
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                        <p>Map failed to load. Enter coordinates manually or check your API key.</p>
                                    </div>
                                )}
                                {!isLoaded && !loadError && (
                                    <div className="aec-map-placeholder">
                                        <svg className="aec-spinner" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                        </svg>
                                        <p>Loading map…</p>
                                    </div>
                                )}

                                {isLoaded && !loadError && (
                                    <div className="aec-map-container">
                                        {/* Autocomplete search bar */}
                                        <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                                            <div className="aec-map-search-wrap">
                                                <svg className="aec-map-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                </svg>
                                                <input
                                                    type="text"
                                                    placeholder="Search for a place or address…"
                                                    className="aec-map-search-input"
                                                />
                                            </div>
                                        </Autocomplete>

                                        {/* Map */}
                                        <GoogleMap
                                            mapContainerStyle={MAP_CONTAINER_STYLE}
                                            center={mapCenter}
                                            zoom={13}
                                            onClick={onMapClick}
                                            onLoad={onMapLoad}
                                            options={{
                                                streetViewControl: false,
                                                mapTypeControl: false,
                                                fullscreenControl: false,
                                            }}
                                        >
                                            {markerPosition && <Marker position={markerPosition} />}
                                        </GoogleMap>

                                        <p className="aec-map-hint">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                            Search for a place above, or click directly on the map to pin the location
                                        </p>
                                    </div>
                                )}

                                {errors.location && <span className="aec-error-msg">{errors.location}</span>}
                            </div>

                            {/* Coordinates (read-only display) */}
                            <div className="aec-field">
                                <label className="aec-label">Longitude</label>
                                <input className="aec-input" type="text" value={hasLocation ? lng.toFixed(6) : "—"} readOnly disabled />
                            </div>
                            <div className="aec-field">
                                <label className="aec-label">Latitude</label>
                                <input className="aec-input" type="text" value={hasLocation ? lat.toFixed(6) : "—"} readOnly disabled />
                            </div>

                            {/* Location badge */}
                            {hasLocation && (
                                <div className="aec-field aec-field--full">
                                    <div className="aec-location-badge">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                        Location pinned · {lat.toFixed(5)}, {lng.toFixed(5)}
                                        {formData.google_url && (
                                            <a href={formData.google_url} target="_blank" rel="noopener noreferrer" className="aec-gmaps-link">
                                                Open in Maps ↗
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Address */}
                            <div className="aec-field aec-field--full">
                                <label className="aec-label">Address <span className="aec-req">*</span></label>
                                <input
                                    className={`aec-input ${errors.address ? "aec-input--error" : ""}`}
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Street, building, area… (auto-filled from map)"
                                    disabled={loading}
                                />
                                {errors.address && <span className="aec-error-msg">{errors.address}</span>}
                            </div>

                            {/* Country */}
                            <div className="aec-field">
                                <label className="aec-label">Country <span className="aec-req">*</span></label>
                                <select className={`aec-select ${errors.country ? "aec-input--error" : ""}`} name="country" value={formData.country} onChange={handleChange} disabled={loading}>
                                    <option value="">Select country</option>
                                    {countryList?.data?.map((c) => (<option key={c._id} value={c._id}>{c.country_name}</option>))}
                                </select>
                                {errors.country && <span className="aec-error-msg">{errors.country}</span>}
                            </div>

                            {/* State */}
                            <div className="aec-field">
                                <label className="aec-label">State <span className="aec-req">*</span></label>
                                <select className={`aec-select ${errors.state ? "aec-input--error" : ""} ${!formData.country ? "aec-select--disabled" : ""}`} name="state" value={formData.state} onChange={handleChange} disabled={!formData.country || loading}>
                                    <option value="">{!formData.country ? "Select country first" : "Select state"}</option>
                                    {states?.data?.map((s) => (<option key={s._id} value={s._id}>{s.state_name}</option>))}
                                </select>
                                {errors.state && <span className="aec-error-msg">{errors.state}</span>}
                            </div>

                            {/* City */}
                            <div className="aec-field">
                                <label className="aec-label">City <span className="aec-req">*</span></label>
                                <select className={`aec-select ${errors.city ? "aec-input--error" : ""} ${!formData.state ? "aec-select--disabled" : ""}`} name="city" value={formData.city} onChange={handleChange} disabled={!formData.state || loading}>
                                    <option value="">{!formData.state ? "Select state first" : "Select city"}</option>
                                    {cities.map((c) => (<option key={c._id} value={c._id}>{c.city_name}</option>))}
                                </select>
                                {errors.city && <span className="aec-error-msg">{errors.city}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="aec-footer">
                        <div className="aec-footer-nav">
                            <button type="button" className="aec-btn aec-btn--ghost" onClick={() => setActiveSection((s) => Math.max(0, s - 1))} disabled={activeSection === 0 || loading || uploadingCover || uploadingVideo}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                                Back
                            </button>
                            {activeSection < sections.length - 1 ? (
                                <button type="button" className="aec-btn aec-btn--primary" onClick={handleNextSection} disabled={loading || uploadingCover || uploadingVideo}>
                                    Next
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                </button>
                            ) : (
                                <button type="button" onClick={handleSubmit} className="aec-btn aec-btn--submit" disabled={loading || uploadingCover || uploadingVideo}>
                                    {loading ? "Saving..." : "Save Experience Center"}
                                </button>
                            )}
                        </div>
                        <Link to="/experience_center" className="aec-btn aec-btn--cancel" onClick={handleReset}>Cancel</Link>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default AddExperienceCenter;